import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Descriptions,
  Image,
  App,
  Spin,
  Carousel,
  Badge,
  List,
  Modal,
  Space,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  HomeOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  FilePdfOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { PropertyArea } from '../../types/property';

import CreditCalculator from '../../components/CreditCalculator';

const { Title, Text, Paragraph } = Typography;

const PropertyAreaDetail: React.FC = () => {
  const [propertyArea, setPropertyArea] = useState<PropertyArea | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { message } = App.useApp();

  const fetchPropertyArea = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/property-areas/${id}`);
      if (response.data && response.data.status === 'success') {
        setPropertyArea(response.data.data);
      } else {
        message.error('Gagal memuat data area properti.');
      }
    } catch (error) {
      console.error('Failed to fetch property area:', error);
      message.error('Terjadi kesalahan saat mengambil data.');
      navigate('/property/areas');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, message]);

  useEffect(() => {
    fetchPropertyArea();
  }, [fetchPropertyArea]);

  const showPreviewModal = (url: string, title: string) => {
    setPreviewUrl(url);
    setPreviewTitle(title);
    setIsModalVisible(true);
  };

  const handleCancelModal = () => {
    setIsModalVisible(false);
    setPreviewUrl('');
    setPreviewTitle('');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!propertyArea) {
    return <div>Area properti tidak ditemukan.</div>;
  }

  const {
    name,
    is_active,
    photos,
    address,
    phone_number,
    description,
    promotion_labels,
    project_info,
    e_brochure_url,
    price_list_url,
  } = propertyArea;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/property/areas')}
            style={{ marginRight: 16 }}
          >
            Kembali
          </Button>
          <Title level={2} style={{ margin: 0, display: 'inline' }}>
            {name}
          </Title>
          <Badge
            status={is_active ? 'success' : 'error'}
            text={is_active ? 'Active' : 'Inactive'}
            style={{ marginLeft: 16 }}
          />
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/property/areas/${id}/edit`)}
            disabled={!is_active}
          >
            Edit Property Area
          </Button>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Card>
            {photos && photos.length > 0 ? (
              <Carousel autoplay draggable>
                {photos.map((photo, index) => (
                  <div key={index}>
                    <Image
                      src={photo.image_url}
                      alt={photo.caption || `Photo ${index + 1}`}
                      style={{
                        width: '100%',
                        height: 400,
                        objectFit: 'cover',
                        borderRadius: 8,
                      }}
                    />
                    {photo.caption && (
                      <Text
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          padding: '8px 12px',
                          textAlign: 'center',
                        }}
                      >
                        {photo.caption}
                      </Text>
                    )}
                  </div>
                ))}
              </Carousel>
            ) : (
              <div
                style={{
                  height: 400,
                  backgroundColor: '#f0f0f0',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <HomeOutlined style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
                <Text type="secondary">Tidak ada foto</Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card>
            <Descriptions column={1} size="middle">
              <Descriptions.Item label="Alamat">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  <Text>{address}</Text>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Nomor Telepon">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  <Text>{phone_number}</Text>
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {(e_brochure_url || price_list_url) && (
            <Card title="Dokumen" style={{ marginTop: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {e_brochure_url && (
                  <Space>
                    <Button
                      icon={<FilePdfOutlined />}
                      onClick={() => showPreviewModal(e_brochure_url, 'E-Brochure')}
                    >
                      Lihat E-Brochure
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      href={e_brochure_url}
                      target="_blank"
                      download
                    >
                      Unduh
                    </Button>
                  </Space>
                )}
                {price_list_url && (
                  <Space>
                    <Button
                      icon={<FilePdfOutlined />}
                      onClick={() => showPreviewModal(price_list_url, 'Price List')}
                    >
                      Lihat Price List
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      href={price_list_url}
                      target="_blank"
                      download
                    >
                      Unduh
                    </Button>
                  </Space>
                )}
              </Space>
            </Card>
          )}

          {promotion_labels && promotion_labels.length > 0 && (
            <Card title="Label Promosi" style={{ marginTop: 16 }}>
              <div>
                {promotion_labels.map((label: string, index: number) => (
                  <Tag color="blue" key={index} style={{ marginBottom: 8 }}>
                    {label}
                  </Tag>
                ))}
              </div>
            </Card>
          )}
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24, alignItems: 'stretch' }}>
        <Col xs={24} lg={14}>
          {description && (
            <Card title="Deskripsi" style={{ marginBottom: 24 }}>
              <Paragraph style={{ fontSize: 16, lineHeight: 1.8 }}>
                {description}
              </Paragraph>
            </Card>
          )}

          {project_info && project_info.length > 0 && (
            <Card title="Info Proyek">
              <List
                dataSource={project_info}
                renderItem={(item: { key: string; value: string }) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<InfoCircleOutlined style={{ color: '#1890ff' }} />}
                      title={item.key}
                      description={item.value}
                    />
                  </List.Item>
                )}
              />
            </Card>
          )}
        </Col>
        <Col xs={24} lg={10} style={{ display: 'flex' }}>
           <CreditCalculator style={{ width: '100%' }} />
        </Col>
      </Row>

      <Modal
        title={previewTitle}
        visible={isModalVisible}
        onCancel={handleCancelModal}
        footer={null}
        width="80%"
        style={{ top: 20 }}
        destroyOnClose
      >
        <iframe
          src={previewUrl}
          style={{ width: '100%', height: '75vh', border: 'none' }}
          title={previewTitle}
        ></iframe>
      </Modal>
    </div>
  );
};

export default PropertyAreaDetail;
